/**
 * 应用设置存储（P0-8：自动保存间隔可配置；亦是未来"设置功能"的地基）。
 *
 * 设计：
 *  · 独立于导演台工程 store——设置是"用户偏好"，不随工程走，单独持久化到 localStorage；
 *  · 纯函数 clampAutosaveInterval / mergeSettings 可离线测，store 只做读写与持久化；
 *  · 只放已确定的项（自动保存间隔），其余设置随"设置功能"里程碑逐个补入，不预先堆空壳。
 */
import { create } from 'zustand';

export interface AppSettings {
  /** 自动保存间隔（秒）：距上次改动后，每隔这么久把工程落一次盘（localStorage + 服务器文件） */
  autosaveIntervalSec: number;
  /** 导演台默认画质档 */
  defaultQuality: 'low' | 'medium' | 'high';
  /** 撤销/重做历史栈深度 */
  historyDepth: number;
  /** AI 生成默认加水印（合规） */
  watermark: boolean;
  /** 主题（当前仅深色真实，浅色占位） */
  theme: 'dark' | 'light';
}

export const DEFAULT_SETTINGS: AppSettings = {
  autosaveIntervalSec: 30,
  defaultQuality: 'medium',
  historyDepth: 100,
  watermark: true,
  theme: 'dark',
};

/** 合法区间：5 秒 ~ 1 小时；非有限值回落默认，避免 0/负数/NaN 把自动保存打死或打爆磁盘 */
export function clampAutosaveInterval(sec: number): number {
  if (!Number.isFinite(sec)) {
    return DEFAULT_SETTINGS.autosaveIntervalSec;
  }
  return Math.min(3600, Math.max(5, Math.round(sec)));
}

/** 撤销栈深度合法区间 20~500。 */
export function clampHistoryDepth(n: number): number {
  if (!Number.isFinite(n)) {
    return DEFAULT_SETTINGS.historyDepth;
  }
  return Math.min(500, Math.max(20, Math.round(n)));
}

const QUALITIES = ['low', 'medium', 'high'] as const;
const THEMES = ['dark', 'light'] as const;

/** 把任意来源（localStorage/导入）的原始值合并到默认之上，逐项校验，坏值回落默认 */
export function mergeSettings(raw: unknown): AppSettings {
  const out: AppSettings = { ...DEFAULT_SETTINGS };
  if (typeof raw === 'object' && raw !== null) {
    const r = raw as Record<string, unknown>;
    if (typeof r.autosaveIntervalSec === 'number') {
      out.autosaveIntervalSec = clampAutosaveInterval(r.autosaveIntervalSec);
    }
    if ((QUALITIES as readonly string[]).includes(r.defaultQuality as string)) {
      out.defaultQuality = r.defaultQuality as AppSettings['defaultQuality'];
    }
    if (typeof r.historyDepth === 'number') {
      out.historyDepth = clampHistoryDepth(r.historyDepth);
    }
    if (typeof r.watermark === 'boolean') {
      out.watermark = r.watermark;
    }
    if ((THEMES as readonly string[]).includes(r.theme as string)) {
      out.theme = r.theme as AppSettings['theme'];
    }
  }
  return out;
}

const SETTINGS_KEY = 'videoai-pro.settings.v1';

function readStored(): AppSettings {
  if (typeof window === 'undefined') {
    return DEFAULT_SETTINGS;
  }
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    return raw === null ? DEFAULT_SETTINGS : mergeSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function writeStored(settings: AppSettings): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* 隐私模式等：静默，内存态仍生效 */
  }
}

interface SettingsState extends AppSettings {
  setAutosaveInterval(sec: number): void;
  setDefaultQuality(q: AppSettings['defaultQuality']): void;
  setHistoryDepth(n: number): void;
  setWatermark(v: boolean): void;
  setTheme(t: AppSettings['theme']): void;
  resetSettings(): void;
}

export const useSettingsStore = create<SettingsState>()((set, get) => ({
  ...readStored(),

  setAutosaveInterval(sec) {
    set({ autosaveIntervalSec: clampAutosaveInterval(sec) });
    writeStored(get());
  },
  setDefaultQuality(q) {
    set({ defaultQuality: q });
    writeStored(get());
  },
  setHistoryDepth(n) {
    set({ historyDepth: clampHistoryDepth(n) });
    writeStored(get());
  },
  setWatermark(v) {
    set({ watermark: v });
    writeStored(get());
  },
  setTheme(t) {
    set({ theme: t });
    writeStored(get());
  },
  resetSettings() {
    set({ ...DEFAULT_SETTINGS });
    writeStored(get());
  },
}));
